import { inject, Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { UserService } from '@core/user/user.service';
import {
    FuseNavigationService,
    FuseVerticalNavigationComponent,
} from '@fuse/components/navigation';
import { ToastService } from '@targx/services/toast/toast.service';
import { Apollo, gql } from 'apollo-angular';
import { DateTime } from 'luxon';
import {
    distinctUntilChanged,
    interval,
    Subject,
    switchMap,
    takeUntil,
    timer,
} from 'rxjs';

// ─── GraphQL queries ──────────────────────────────────────────────────────────

const APPOINTMENT_REQUESTS_POLL = gql`
  query BgAppointmentRequestsPoll($where: AppointmentRequestWhereInput) {
    AppointmentRequestFindMany(
      where: $where
      take: 50
      orderBy: { startDate: desc }
    ) {
      items {
        id
        startDate
        endDate
        client {
          id
          name
        }
        speciality {
          name
        }
      }
    }
  }
`;

const CLIENTS_PENDING_POLL = gql`
  query BgClientsPendingPoll($where: ClientWhereInput) {
    ClientFindMany(where: $where, take: 50) {
      items {
        id
        name
      }
    }
  }
`;

// ─── Intervals ────────────────────────────────────────────────────────────────

const POLL_REQUESTS_MS = 30_000;
const POLL_CLIENTS_MS  = 60_000;

// ─── Service ─────────────────────────────────────────────────────────────────

@Injectable({ providedIn: 'root' })
export class BackgroundJobsService {
  private readonly _apollo        = inject(Apollo).use('managment');
  private readonly _toast         = inject(ToastService);
  private readonly _router        = inject(Router);
  private readonly _fuseNavigation = inject(FuseNavigationService);
  private readonly _userService   = inject(UserService);

  private _lastClientsBadgeCount = 0;

  constructor() {
    this._userService.user$
      .pipe(distinctUntilChanged((a, b) => a?.id === b?.id))
      .subscribe(user => {
        this.stop();
        if (user?.id) {
          this.start();
        }
      });
  }

  /** IDs already notified — reset on stop() */
  private _seenRequestIds  = new Set<number>();
  private _seenClientIds   = new Set<number>();
  private _requestsSeedDone = false;

  private _destroy$ = new Subject<void>();

  // ─── Public API ────────────────────────────────────────────────────────────

  start(): void {
    this._requestsSeedDone = false;
    this._pollNewRequests();
    this._pollPendingClients();
  }

  stop(): void {
    this._destroy$.next();
    this._destroy$.complete();
    this._destroy$ = new Subject<void>();
    this._seenRequestIds.clear();
    this._seenClientIds.clear();
    this._requestsSeedDone = false;
    this._updateClientsBadge(0);
  }

  // ─── Job 1 — New appointment requests (every 30 s) ─────────────────────────

  private _pollNewRequests(): void {
    const where = {
      status: { equals: 'WAITING_PAYMENT' },
    };

    interval(POLL_REQUESTS_MS)
      .pipe(
        takeUntil(this._destroy$),
        switchMap(() =>
          this._apollo.query<any>({
            query: APPOINTMENT_REQUESTS_POLL,
            variables: { where },
            fetchPolicy: 'network-only',
          })
        )
      )
      .subscribe({
        next: ({ data }) => {
          const items: any[] = data?.AppointmentRequestFindMany?.items ?? [];

          if (!this._requestsSeedDone) {
            items.forEach(req => this._seenRequestIds.add(req.id));
            this._requestsSeedDone = true;
            return;
          }

          for (const req of items) {
            if (this._seenRequestIds.has(req.id)) continue;
            this._seenRequestIds.add(req.id);

            const timeStr =
              req.startDate && req.endDate
                ? `${this._fmt(req.startDate)} – ${this._fmt(req.endDate, 'HH:mm')}`
                : req.startDate
                  ? this._fmt(req.startDate)
                  : undefined;

            this._toast.show({
              type: 'request',
              icon: 'heroicons_outline:calendar-days',
              label: 'Novo Agendamento',
              title: req.client?.name ?? 'Novo pedido de consulta',
              subtitle: req.speciality?.name ?? undefined,
              time: timeStr,
              actions: [
                {
                      label: 'Ver Mais',
                  style: 'primary',
                  onClick: () =>
                    this._router.navigate(['/appointments'], {
                      queryParams: { openDetail: req.id },
                    }),
                },
                {
                  label: 'Ver mais tarde',
                  style: 'neutral',
                  onClick: () => { /* dismiss only */ },
                },
              ],
            });
          }
        },
        error: () => { /* silent — background job */ },
      });
  }

  // ─── Job 2 — Pending clients (every 60 s) ──────────────────────────────────

  private _pollPendingClients(): void {
    const where = {
      Client2Specialist: {
        some: {
          isActive: { equals: false },
        },
      },
    };

    timer(0, POLL_CLIENTS_MS)
      .pipe(
        takeUntil(this._destroy$),
        switchMap(() =>
          this._apollo.query<any>({
            query: CLIENTS_PENDING_POLL,
            variables: { where },
            fetchPolicy: 'network-only',
          })
        )
      )
      .subscribe({
        next: ({ data }) => {
          const items: any[] = data?.ClientFindMany?.items ?? [];

          this._updateClientsBadge(items.length);

          const isFirstPoll = this._seenClientIds.size === 0 && items.length > 0;
          if (isFirstPoll) {
            items.forEach(c => this._seenClientIds.add(c.id));
            return;
          }

          for (const client of items) {
            if (this._seenClientIds.has(client.id)) continue;
            this._seenClientIds.add(client.id);

            this._toast.show({
              type: 'client',
              icon: 'heroicons_outline:user-plus',
              title: client.name ?? 'Novo cliente',
              subtitle: 'Novo cliente pendente de aprovação',
              actions: [
                {
                  label: 'Ver perfil',
                  style: 'primary',
                  onClick: () => this._router.navigate(['/clients', client.id]),
                },
              ],
            });
          }
        },
        error: () => { /* silent */ },
      });
  }

  // ─── Helpers ───────────────────────────────────────────────────────────────

  private _updateClientsBadge(count: number): void {
    this._lastClientsBadgeCount = count;
    const navComponent =
      this._fuseNavigation.getComponent<FuseVerticalNavigationComponent>('mainNavigation');
    if (!navComponent) {
      setTimeout(() => this._updateClientsBadge(this._lastClientsBadgeCount), 300);
      return;
    }
    const item = this._fuseNavigation.getItem('main.clients', navComponent.navigation);
    if (!item) return;
    item.badge =
      count > 0
        ? { title: String(count), classes: 'px-2 bg-warn text-on-warn rounded-full' }
        : undefined;
    navComponent.refresh();
  }

  private _fmt(iso: string, pattern = 'dd MMM · HH:mm'): string {
    return DateTime.fromISO(iso).setZone('UTC').toFormat(pattern);
  }
}
